// Typed fetch client. ALL backend calls go through here
// (docs/FRONTEND.md): owns error mapping and 401 redirect.

import type {
  ApiErrorBody,
  MeetingEvent,
  IngestOutcome,
  JoinInfo,
  JoinResult,
  Meeting,
  MeetingDetail,
  Participant,
  RtcToken,
  SearchHit,
  Segment,
  SummaryRow,
  User,
} from "./types";

/// Guest credentials ride a query param (no cookie session — D14).
function gtSuffix(gt?: string | null): string {
  return gt ? `?gt=${encodeURIComponent(gt)}` : "";
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(path, {
    credentials: "same-origin",
    ...init,
  });
  if (resp.status === 401 && !path.startsWith("/api/auth/")) {
    location.hash = "#/login";
    throw new ApiError(401, "unauthorized", "please sign in");
  }
  if (!resp.ok) {
    let code = "error";
    let message = `request failed (${resp.status})`;
    try {
      const body = (await resp.json()) as ApiErrorBody;
      code = body.error.code;
      message = body.error.message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(resp.status, code, message);
  }
  return (await resp.json()) as T;
}

function json(body: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  };
}

// ---- auth ----

export const auth = {
  register: (email: string, password: string) =>
    request<{ user: User }>("/api/auth/register", json({ email, password })),
  login: (email: string, password: string) =>
    request<{ user: User }>("/api/auth/login", json({ email, password })),
  logout: () => request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  me: () =>
    request<{
      user: User;
      provider: string;
      registration_open: boolean;
      hosted_meetings: boolean;
    }>("/api/auth/me"),
};

// ---- meetings ----

export const meetings = {
  list: () => request<{ meetings: Meeting[] }>("/api/meetings"),
  create: (title?: string) =>
    request<{ meeting: Meeting }>("/api/meetings", json({ title })),
  detail: (id: number) => request<MeetingDetail>(`/api/meetings/${id}`),
  rename: (id: number, title: string) =>
    request<{ meeting: Meeting }>(`/api/meetings/${id}`, {
      ...json({ title }),
      method: "PATCH",
    }),
  remove: (id: number) =>
    request<{ ok: boolean }>(`/api/meetings/${id}`, { method: "DELETE" }),
  stop: (id: number) =>
    request<{ status: string }>(`/api/meetings/${id}/stop`, { method: "POST" }),
  summarize: (id: number) =>
    request<{ status: string }>(`/api/meetings/${id}/summarize`, {
      method: "POST",
    }),
};

// ---- hosted meetings (plan 0002) ----

export const hosted = {
  create: (title?: string) =>
    request<{ meeting: Meeting; participant: Participant }>(
      "/api/meetings/hosted",
      json({ title }),
    ),
  joinInfo: (code: string) =>
    request<JoinInfo>(`/api/join/${encodeURIComponent(code)}`),
  join: (code: string, guestName?: string) =>
    request<JoinResult>(
      `/api/join/${encodeURIComponent(code)}`,
      json({ guest_name: guestName }),
    ),
  participants: (meetingId: number, gt?: string | null) =>
    request<{ participants: Participant[] }>(
      `/api/meetings/${meetingId}/participants${gtSuffix(gt)}`,
    ),
  rtcToken: (meetingId: number, gt?: string | null) =>
    request<RtcToken>(`/api/meetings/${meetingId}/rtc-token${gtSuffix(gt)}`, {
      method: "POST",
    }),
  leave: (meetingId: number, gt?: string | null) =>
    request<{ ok: boolean }>(`/api/meetings/${meetingId}/leave${gtSuffix(gt)}`, {
      method: "POST",
    }),
  end: (meetingId: number) =>
    request<{ status: string }>(`/api/meetings/${meetingId}/end`, {
      method: "POST",
    }),
  resetCode: (meetingId: number) =>
    request<{ join_code: string }>(`/api/meetings/${meetingId}/reset-code`, {
      method: "POST",
    }),
  mute: (meetingId: number, pid: number, muted: boolean) =>
    request<{ ok: boolean }>(
      `/api/meetings/${meetingId}/participants/${pid}/mute`,
      json({ muted }),
    ),
  setMic: (meetingId: number, pid: number, canPublish: boolean) =>
    request<{ ok: boolean }>(
      `/api/meetings/${meetingId}/participants/${pid}/mic`,
      json({ can_publish: canPublish }),
    ),
  kick: (meetingId: number, pid: number) =>
    request<{ ok: boolean }>(`/api/meetings/${meetingId}/participants/${pid}`, {
      method: "DELETE",
    }),
};

// ---- transcription ----

export function uploadChunk(
  meetingId: number,
  seq: number,
  startOffsetMs: number,
  wav: Blob,
  gt?: string | null,
): Promise<IngestOutcome> {
  const form = new FormData();
  form.set("seq", String(seq));
  const offsetMs = Math.max(0, Math.round(startOffsetMs));
  form.set("start_offset_ms", String(Number.isFinite(offsetMs) ? offsetMs : 0));
  form.set("audio", wav, `chunk-${seq}.wav`);
  return request<IngestOutcome>(`/api/meetings/${meetingId}/chunks${gtSuffix(gt)}`, {
    method: "POST",
    body: form,
  });
}

/// REST segment fetch — bootstrap + fallback when a proxy starves the
/// SSE stream (plan 0002 troubleshooting).
export function fetchSegments(
  meetingId: number,
  afterMs: number | null,
  gt?: string | null,
): Promise<Segment[]> {
  const params = new URLSearchParams();
  if (afterMs !== null) params.set("after_ms", String(afterMs));
  if (gt) params.set("gt", gt);
  const qs = params.toString();
  return request<Segment[]>(
    `/api/meetings/${meetingId}/segments${qs ? `?${qs}` : ""}`,
  );
}

export function fetchTimelineEvents(
  meetingId: number,
  gt?: string | null,
): Promise<MeetingEvent[]> {
  return request<{ events: MeetingEvent[] }>(
    `/api/meetings/${meetingId}/timeline-events${gtSuffix(gt)}`,
  ).then((r) => r.events);
}

// ---- admin portal (plan 0004; X-Admin-Token header) ----

function adminRequest<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  return request<T>(path, {
    ...init,
    headers: { ...(init?.headers ?? {}), "x-admin-token": token },
  });
}

export const admin = {
  overview: (token: string) =>
    adminRequest<{ overview: Record<string, unknown> }>("/api/admin/overview", token),
  users: (token: string) =>
    adminRequest<{ users: AdminUserRow[] }>("/api/admin/users", token),
  meetings: (token: string) =>
    adminRequest<{ meetings: AdminMeetingRow[] }>("/api/admin/meetings", token),
  setDisabled: (token: string, userId: number, disabled: boolean) =>
    adminRequest<{ ok: boolean }>(`/api/admin/users/${userId}/disabled`, token, {
      method: "POST",
      headers: { "content-type": "application/json", "x-admin-token": token },
      body: JSON.stringify({ disabled }),
    }),
};

export interface AdminUserRow {
  id: number;
  email: string;
  created_at: number;
  disabled: boolean;
  meeting_count: number;
  billed_stt_seconds: number;
  last_session_at: number | null;
}

export interface AdminMeetingRow {
  id: number;
  title: string;
  owner_email: string;
  kind: string;
  state: string;
  billed_stt_seconds: number;
  participant_count: number;
  segment_count: number;
  created_at: number;
}

export const search = (q: string) =>
  request<SearchHit[]>(`/api/search?q=${encodeURIComponent(q)}`);

// ---- import ----

export function importFile(file: File, title?: string): Promise<{ meeting: Meeting }> {
  const form = new FormData();
  if (title) form.set("title", title);
  form.set("file", file, file.name);
  return request<{ meeting: Meeting }>("/api/imports", {
    method: "POST",
    body: form,
  });
}

// ---- summary speech (returns a playable object URL) ----

export async function summarySpeechUrl(meetingId: number): Promise<string> {
  const resp = await fetch(`/api/meetings/${meetingId}/summary/speech`, {
    method: "POST",
    credentials: "same-origin",
  });
  if (!resp.ok) {
    let message = `speech failed (${resp.status})`;
    try {
      message = ((await resp.json()) as ApiErrorBody).error.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(resp.status, "speech_failed", message);
  }
  return URL.createObjectURL(await resp.blob());
}

export type { SummaryRow };
